<template>
  <v-dialog
    width="800"
    v-model="opened"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="$emit('update:opened', false)"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="title py-3">
        <v-layout align-start> Add To-do </v-layout>

        <v-spacer />
      </v-card-title>

      <v-card-text class="py-3 text-xs-left">
        <div class="mb-4">
          <h4 class="label">Title</h4>
          <v-text-field
            v-model="title"
            class="default-input"
            placeholder="Title"
            solo
            hide-details
          />
        </div>

        <div class="mb-4">
          <h4 class="label">Description</h4>
          <quill-editor
            v-model="description"
            :options="editorOptions"
            class="description-editor"
          />
        </div>

        <div class="mb-4">
          <h4 class="label">Related entity</h4>

          <v-autocomplete
            class="default-input body-1"
            hide-details
            :loading="loadingCompanies"
            :disabled="loadingCompanies"
            solo
            :items="entitiesWithClient"
            v-model="selectedEntityId"
            placeholder="Select entity"
            item-text="name"
            item-value="id"
          />
        </div>

        <div class="mb-4">
          <h4 class="label">Assigned to</h4>

          <v-autocomplete
            class="default-input body-1"
            hide-details
            solo
            :items="firmUsers"
            v-model="selectedAssignedUserId"
            placeholder="Select assignee"
            item-text="name"
            item-value="id"
          />
        </div>

        <!--
        <div class="mb-4">
          <h4 class="label">Priority</h4>

          <v-select
            class="default-input body-1"
            hide-details
            solo
            :items="selectablePriorities"
            v-model="selectedPriority"
            placeholder="Select priority"
          />
        </div>
        -->

        <!--
        <div class="mb-4">
          <h4 class="label">Tags</h4>
          <v-combobox
            class="default-input"
            placeholder="Enter tags (press Enter to insert tags)"
            multiple
            chips
            small-chips
            solo
            hide-details
          />
        </div>
        -->
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          flat
          class="text-capitalize"
          @click="$emit('update:opened', false)"
        >
          Cancel
        </v-btn>
        <v-btn
          class="text-capitalize"
          color="primary"
          @click="addTodo()"
          :disabled="!canSave"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import axios from 'axios';
import { taskPriority } from '@/constants/variables';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';

export default {
  components: {},

  props: {
    opened: {
      type: Boolean,
    },
  },

  data() {
    return {
      firmUsers: [],

      title: '',
      description: '',
      selectedPriority: taskPriority.Medium,
      selectedEntityId: null,
      selectedAssignedUserId: null,

      editorOptions: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['image'],
          ],
        },
        placeholder: 'Description',
      },
    };
  },

  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),
    ...mapGetters({
      currentUser: 'user',
    }),

    canSave() {
      return (
        !isEmpty(this.title) &&
        this.selectedPriority &&
        this.selectedEntityId &&
        this.selectedAssignedUserId
      );
    },

    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            c.user &&
            (!this.currentUser.overview_setting.show_assigned_clients ||
              c.user.assigned_advisor_id === this.currentUser.id),
        )
        .map(entity => ({
          id: entity.id,
          name: `${this.getDisplayName(entity)}${
            entity.user && entity.user.for_testing ? ' (Test)' : ''
          }`,
          entity_type: entity.entity_type,
          for_testing: entity.user ? entity.user.for_testing : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    selectablePriorities() {
      return Object.entries(taskPriority).map(([text, value]) => {
        return { text, value };
      });
    },
  },

  watch: {
    opened() {
      this.resetForm();
    },
  },

  methods: {
    resetForm() {
      this.title = '';
      this.description = '';
      this.selectedPriority = taskPriority.Medium;
      this.selectedEntityId = null;
      this.selectedAssignedUserId = null;
    },

    async addTodo() {
      const response = await axios.post('/todo_tasks', {
        company_id: this.selectedEntityId,
        title: this.title,
        description: this.description,
        priority: this.selectedPriority,
        assigned_user_id: this.selectedAssignedUserId,
      });

      this.$emit('update:opened', false);
      this.$emit('todo-added');
    },

    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },

    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },

    async getAllFirmUsers() {
      const response = await API.firm.getAllUsers();
      this.firmUsers = response.data.users;
    },
  },

  async created() {
    await this.getAllFirmUsers();
  },
};
</script>

<style lang="postcss" scoped>
.default-input {
  & >>> .v-input__slot {
    border: 1px solid #ccc;
  }
}

.description-editor .ql-editor {
  min-height: 200px;
}

.title {
  font-weight: bold;
  letter-spacing: 0px !important;
  border-bottom: 1px solid #ccc;
}

.label {
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 500;
  color: #777;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
</style>
